import { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material'

import axios, { pluckData } from 'src/utilities/http-client'
import { useAppSelector } from 'src/store/hooks'
import {
  QuestionSessionData,
  QuizSessionAnswer,
  QuizSessionResult,
  QuizV2,
} from 'src/v3/core/types'
import {
  QuizAnswerFeedback,
  QuizTaker,
} from 'src/v3/components/quiz/quiz-taker'
import { CloseIcon, ConfirmIcon } from 'src/v3/core/icons'
import { selectUserID } from 'src/v3/settings/settings.slice'

// The events adapter for the now-presentational QuizTaker. Everything the old
// QuizTaker did itself — the session POST, the per-answer POST, scoring, and the
// summary screen — lives here. The caller picks the session contract via
// apiPath: V1 pages use /api/event, while V2 uses /api/event/v2/:eventID, whose
// answer and result routes also create the CE record on a pass. It doesn't
// share its fetching with classroom-v2, which needs entirely different
// endpoints and keeps its state in Redux for compliance reasons.
//
// classroom-v2's equivalent wiring is classroom-v2/components/quiz-step.tsx.
// The two deliberately don't share a container, only the presentation.

type ResultWithAnswers = QuizSessionResult & { answers: QuizSessionAnswer[] }

export let EventQuiz = ({
  name,
  quiz,
  questionData,
  apiPath,
  proceedButton,
  existingResult,
}: {
  name: string
  quiz: QuizV2
  questionData: QuestionSessionData[]
  apiPath: string
  proceedButton: ReactElement
  existingResult?: ResultWithAnswers
}): ReactElement => {
  let navigate = useNavigate()
  let userID = useAppSelector(selectUserID)

  let [sessionID, setSessionID] = useState<string>('')
  let [questionIndex, setQuestionIndex] = useState<number>(0)
  let [selectedAnswerID, setSelectedAnswerID] = useState<string | null>(null)
  let [feedback, setFeedback] = useState<QuizAnswerFeedback | null>(null)
  let [quizResult, setQuizResult] = useState<ResultWithAnswers>()
  let [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  let [isAdvancing, setIsAdvancing] = useState<boolean>(false)

  let currentResult = quizResult || existingResult
  let current = questionData?.[questionIndex]
  let isLastQuestion = questionIndex + 1 === questionData?.length

  useEffect(() => {
    if (quiz?.id && userID && !sessionID) {
      axios
        .post<string>(`${apiPath}/quiz/session`, { quizID: quiz.id, userID })
        .then(pluckData)
        .then((id) => setSessionID(id))
    }
  }, [quiz, userID, sessionID])

  useEffect(() => {
    if (questionData) {
      let i = questionData.findIndex((qd) => qd.isCurrent)
      if (i > -1) setQuestionIndex(i)
    }
  }, [questionData])

  // Every write below needs a sessionID, and the POST that produces one is in
  // flight from mount — so a fast click on an already-loaded question could
  // otherwise post an empty sessionID and get a 404 back, leaving the answer
  // unrecorded with no way to retry it. The taker doesn't render until the
  // session lands (see below), so these guards are the backstop rather than the
  // only defense. An absent userID is covered too: without it the session POST
  // never fires, so sessionID stays empty.
  let handleSubmitAnswer = () => {
    if (!sessionID || !selectedAnswerID || !current) return
    setIsSubmitting(true)
    axios
      .post<QuizAnswerFeedback>(`${apiPath}/quiz/session/answer`, {
        sessionID,
        questionID: current.quizQuestion.id,
        answerID: selectedAnswerID,
        userID,
      })
      .then(pluckData)
      .then(setFeedback)
      .finally(() => setIsSubmitting(false))
  }

  // Selection and feedback are cleared only once the advance SUCCEEDS, together
  // with the index — they describe the question being left behind, so dropping
  // them while the POST is still in flight would strand a failed advance on the
  // current question with its answer key gone. The user would be looking at an
  // unanswered-looking question they've already been scored on, and re-answering
  // it is a second recorded answer for the same question.
  //
  // Scoring is different: there's no next question to land on, so the summary
  // page replaces this surface entirely and there's nothing to clear.
  let handleAdvance = () => {
    if (!sessionID) return
    setIsAdvancing(true)
    if (isLastQuestion) {
      axios
        .post<ResultWithAnswers>(`${apiPath}/quiz/session/result`, {
          sessionID,
        })
        .then(pluckData)
        .then(setQuizResult)
        .finally(() => setIsAdvancing(false))
      return
    }
    axios
      .post(`${apiPath}/quiz/session/question`, {
        sessionID,
        questionID: questionData[questionIndex + 1].quizQuestion.id,
      })
      .then(() => {
        setQuestionIndex(questionIndex + 1)
        setSelectedAnswerID(null)
        setFeedback(null)
      })
      .finally(() => setIsAdvancing(false))
  }

  let showSummaryPage = !!quizResult || existingResult?.passed

  return (
    <>
      <Typography variant="h5">{name} Quiz</Typography>
      {!showSummaryPage ? (
        // Waits on the session as well as the question: offering answer choices
        // that can't yet be recorded is the actual bug, so the fix is not
        // showing them until a submit would succeed. The session POST lives in
        // local state rather than Redux, so the parent's <Loading> doesn't cover
        // this gap — hence the explicit spinner.
        !current || !sessionID ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 2 }}>
            <QuizTaker
              question={current.quizQuestion}
              answers={current.questionAnswers}
              questionNumber={questionIndex + 1}
              totalQuestions={questionData.length}
              selectedAnswerID={selectedAnswerID}
              feedback={feedback}
              isSubmitting={isSubmitting}
              advanceLabel={isLastQuestion ? 'See Results' : 'Next Question'}
              isAdvancing={isAdvancing}
              userID={userID}
              onSelectAnswer={setSelectedAnswerID}
              onSubmitAnswer={handleSubmitAnswer}
              onAdvance={handleAdvance}
            />
          </Box>
        )
      ) : (
        <>
          <Typography sx={{ mt: 2, mb: 1 }}>
            {currentResult?.passed
              ? 'Congratulations! You have passed this quiz!'
              : 'You did not pass this quiz. To get credit, you will need to retake the quiz.'}
          </Typography>
          {currentResult?.passed && <Box>{proceedButton}</Box>}
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Question Review
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {currentResult?.answers.map((a) => (
              <Card key={a.id}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5 }}
                >
                  <Box sx={{ pb: '3px' }}>
                    {a.isCorrect ? (
                      <ConfirmIcon color="success" />
                    ) : (
                      <CloseIcon color="error" />
                    )}
                  </Box>
                  <Typography>
                    {
                      questionData?.find(
                        (qd) => qd.quizQuestion.id === a.questionID,
                      )?.quizQuestion.text
                    }
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>
          {!currentResult?.passed && (
            <Button
              sx={{ mt: 2, textTransform: 'none' }}
              variant="contained"
              onClick={() => navigate(0)}
            >
              Restart Quiz
            </Button>
          )}
        </>
      )}
    </>
  )
}
